/**
 * Shared asset filter engine used by AssetManagement and dynamic asset views.
 */

export interface AssetForFilter {
  asset_id: string;
  asset_type_id: string;
  location_id: string;
  status: string;
  is_loaned_out?: boolean;
  is_overdue?: boolean;
  is_out_of_action?: boolean;
  locked_by_user_id?: string | null;
  primary_user_id?: string | null;
  job_tags?: Array<{ job_tag_id: string }>;
  // search fields
  asset_name: string;
  serial_number?: string;
  asset_type_name?: string;
  location_name?: string;
  number_plate?: string;
  manufacturer?: string;
  model?: string;
  description?: string;
}

export interface AssetFilters {
  assetType: string;      // 'all' | asset_type_id
  location: string;       // 'all' | location_id
  loanStatus: string;     // 'all' | 'loaned_out' | 'available' | 'overdue'
  status: string;         // 'all' | 'active' | 'inactive'
  outOfAction: string;    // 'all' | 'true' | 'false'
  lockStatus: string;     // 'all' | 'locked' | 'unlocked'
  jobTag: string;         // 'all' | 'my_tags' | job_tag_id
  excludeJobTagIds?: string[];  // exclude assets that have ANY of these tags
}

/** View object as stored in businesses.asset_views */
export interface BusinessAssetView {
  id: string;
  label: string;
  sortOrder: number;
  showInWeb: boolean;
  showInMobile: boolean;
  /** Stable icon key — see constants/assetViewIcons.ts */
  icon?: string;
  filters: Partial<AssetFilters> & { searchQuery?: string };
}

export const DEFAULT_ASSET_FILTERS: AssetFilters = {
  assetType: 'all',
  location: 'all',
  loanStatus: 'all',
  status: 'all',
  outOfAction: 'all',
  lockStatus: 'all',
  jobTag: 'all',
  excludeJobTagIds: [],
};

export interface ApplyFiltersContext {
  searchQuery: string;
  userJobTagIds: string[];
  currentUserId: string | null;
}

/**
 * Apply all filters + search to an array of assets.
 * Returns a filtered (and optionally sorted) copy.
 */
export function applyAssetFilters<T extends AssetForFilter>(
  assets: T[],
  filters: AssetFilters,
  ctx: ApplyFiltersContext
): T[] {
  let filtered = [...assets];

  if (ctx.searchQuery.trim()) {
    const q = ctx.searchQuery.toLowerCase();
    filtered = filtered.filter(a =>
      a.asset_name.toLowerCase().includes(q) ||
      a.serial_number?.toLowerCase().includes(q) ||
      a.asset_type_name?.toLowerCase().includes(q) ||
      a.location_name?.toLowerCase().includes(q) ||
      a.number_plate?.toLowerCase().includes(q) ||
      a.manufacturer?.toLowerCase().includes(q) ||
      a.model?.toLowerCase().includes(q) ||
      a.description?.toLowerCase().includes(q)
    );
  }

  if (filters.assetType !== 'all') {
    filtered = filtered.filter(a => a.asset_type_id === filters.assetType);
  }

  if (filters.location !== 'all') {
    filtered = filtered.filter(a => a.location_id === filters.location);
  }

  if (filters.loanStatus === 'loaned_out') {
    filtered = filtered.filter(a => a.is_loaned_out === true);
  } else if (filters.loanStatus === 'available') {
    filtered = filtered.filter(a => !a.is_loaned_out);
  } else if (filters.loanStatus === 'overdue') {
    filtered = filtered.filter(a => a.is_overdue === true);
  }

  if (filters.status !== 'all') {
    filtered = filtered.filter(a => a.status === filters.status);
  }

  if (filters.outOfAction === 'true') {
    filtered = filtered.filter(a => a.is_out_of_action === true);
  } else if (filters.outOfAction === 'false') {
    filtered = filtered.filter(a => !a.is_out_of_action);
  }

  if (filters.lockStatus === 'locked') {
    filtered = filtered.filter(a => !!a.locked_by_user_id);
  } else if (filters.lockStatus === 'unlocked') {
    filtered = filtered.filter(a => !a.locked_by_user_id);
  }

  if (filters.jobTag === 'my_tags' && ctx.userJobTagIds.length > 0) {
    const mySet = new Set(ctx.userJobTagIds.map(String));
    filtered = filtered.filter(a =>
      (a.job_tags || []).some(t => mySet.has(String(t.job_tag_id)))
    );
  } else if (filters.jobTag !== 'all' && filters.jobTag !== 'my_tags') {
    filtered = filtered.filter(a =>
      (a.job_tags || []).some(t => String(t.job_tag_id) === String(filters.jobTag))
    );
  }

  if (filters.excludeJobTagIds?.length) {
    const exclude = new Set(filters.excludeJobTagIds.map(String));
    filtered = filtered.filter(a =>
      !(a.job_tags || []).some(t => exclude.has(String(t.job_tag_id)))
    );
  }

  // Default sort: primary user's assets first, then job-tagged assets
  const isDefaultView =
    !ctx.searchQuery.trim() &&
    filters.assetType === 'all' &&
    filters.location === 'all' &&
    filters.loanStatus === 'all' &&
    filters.status === 'all' &&
    filters.outOfAction === 'all' &&
    filters.lockStatus === 'all' &&
    (filters.jobTag === 'all' || filters.jobTag === 'my_tags') &&
    !filters.excludeJobTagIds?.length;

  if (isDefaultView && ctx.currentUserId) {
    const myTagSet = new Set(ctx.userJobTagIds.map(String));
    filtered = [...filtered].sort((a, b) => {
      const aPrimary = a.primary_user_id && String(a.primary_user_id) === String(ctx.currentUserId) ? 0 : 1;
      const bPrimary = b.primary_user_id && String(b.primary_user_id) === String(ctx.currentUserId) ? 0 : 1;
      if (aPrimary !== bPrimary) return aPrimary - bPrimary;
      const aTagged = myTagSet.size > 0 && (a.job_tags || []).some(t => myTagSet.has(String(t.job_tag_id))) ? 0 : 1;
      const bTagged = myTagSet.size > 0 && (b.job_tags || []).some(t => myTagSet.has(String(t.job_tag_id))) ? 0 : 1;
      return aTagged - bTagged;
    });
  }

  return filtered;
}

/**
 * Convert a stored BusinessAssetView's filters into runtime AssetFilters state.
 * Any missing filter keys fall back to the DEFAULT_ASSET_FILTERS value.
 */
export function filtersFromAssetView(view: BusinessAssetView): { filters: AssetFilters; searchQuery: string } {
  const f = view.filters ?? {};
  return {
    filters: {
      assetType: f.assetType ?? 'all',
      location: f.location ?? 'all',
      loanStatus: f.loanStatus ?? 'all',
      status: f.status ?? 'all',
      outOfAction: f.outOfAction ?? 'all',
      lockStatus: f.lockStatus ?? 'all',
      jobTag: f.jobTag ?? 'all',
      excludeJobTagIds: f.excludeJobTagIds ?? [],
    },
    searchQuery: f.searchQuery ?? '',
  };
}

/** Return true when all filters are at their default (unfiltered) state. */
export function isFiltersDefault(filters: AssetFilters, searchQuery: string): boolean {
  return (
    !searchQuery.trim() &&
    filters.assetType === 'all' &&
    filters.location === 'all' &&
    filters.loanStatus === 'all' &&
    filters.status === 'all' &&
    filters.outOfAction === 'all' &&
    filters.lockStatus === 'all' &&
    (filters.jobTag === 'all' || filters.jobTag === 'my_tags') &&
    !filters.excludeJobTagIds?.length
  );
}
